import { cn } from '@osteopro/ui';
import { Stethoscope, User, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { consultationTabPath } from './consultation-tab-path.js';
import { usePatientTabs } from './patient-tabs.js';
import type { ConsultationTabInfo, OpenTab } from './patient-tabs-context.js';
import { UnsavedChangesDialog } from './UnsavedChangesDialog.js';

/** Date de séance telle que le champ la montre : le jour civil local, pas le jour UTC. */
function formatLocalDate(value: string, language: string): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(date);
}

function consultationLabel(
  tab: ConsultationTabInfo,
  labelOf: (date: string) => string,
  pending: string,
  language: string,
): string {
  if (tab.startedAt === null || tab.startedAt === '') return pending;
  const date = formatLocalDate(tab.startedAt, language);
  return date === '' ? pending : labelOf(date);
}

function tabHref(tab: OpenTab): string {
  return tab.kind === 'patient'
    ? `/patients/${tab.id}`
    : consultationTabPath(tab.id, tab.section, tab.search);
}

function isCurrent(tab: OpenTab, location: string): boolean {
  return tab.kind === 'patient'
    ? location === `/patients/${tab.id}`
    : location.startsWith(`/consultations/${tab.id}`);
}

/** Barre d'onglets des dossiers et des consultations ouverts. Point d'alerte si l'onglet est sale. */
export function PatientTabsBar() {
  const { t, i18n } = useTranslation();
  const [location, navigate] = useLocation();
  const {
    tabs,
    activeTabId,
    setActiveTab,
    closeTab,
    isDirty,
    dirtyRegions,
    saveRegion,
    discardRegion,
  } = usePatientTabs();
  const [closing, setClosing] = useState<string | null>(null);

  if (tabs.length === 0) return null;

  const labelFor = (tab: OpenTab): string =>
    tab.kind === 'patient'
      ? `${tab.firstName} ${tab.lastName}`
      : consultationLabel(
          tab,
          (date) => t('shell:tabs.consultation_of', { date }),
          t('shell:tabs.consultation_pending'),
          i18n.language,
        );

  const leave = (tabId: string) => {
    const tab = tabs.find((item) => item.id === tabId);
    if (!tab || !isCurrent(tab, location)) return;
    const remaining = tabs.filter((item) => item.id !== tabId);
    const next = remaining.at(-1);
    navigate(next ? tabHref(next) : '/patients');
  };

  const requestClose = (tabId: string) => {
    if (isDirty(tabId)) {
      setClosing(tabId);
      return;
    }
    leave(tabId);
    closeTab(tabId);
  };

  const closingTab = tabs.find((tab) => tab.id === closing);
  const snapshot =
    closing && closingTab
      ? [
          {
            patientId: closing,
            patientName:
              closingTab.kind === 'patient'
                ? `${closingTab.lastName} ${closingTab.firstName}`
                : labelFor(closingTab),
            regions: dirtyRegions(closing),
          },
        ]
      : [];

  return (
    <>
      <div className="w-full border-b bg-background" data-testid="patient-tabs-bar">
        <div className="flex items-center gap-1 overflow-x-auto px-6">
          {tabs.map((tab) => {
            const Icon = tab.kind === 'patient' ? User : Stethoscope;
            const label = labelFor(tab);
            return (
              <button
                key={tab.id}
                type="button"
                title={label}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(tabHref(tab));
                }}
                className={cn(
                  'group relative flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors hover-elevate',
                  activeTabId === tab.id
                    ? 'border-primary bg-muted/50 text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
                data-testid={
                  tab.kind === 'patient' ? `tab-patient-${tab.id}` : `tab-consultation-${tab.id}`
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="max-w-[150px] truncate">{label}</span>
                {isDirty(tab.id) && (
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full bg-warning"
                    title={t('shell:tabs.unsaved')}
                    data-testid={`indicator-unsaved-${tab.id}`}
                  />
                )}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={t('shell:tabs.close')}
                  className="-mr-1 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm opacity-0 transition-colors hover:bg-destructive/20 hover:text-destructive group-hover:opacity-100"
                  onClick={(event) => {
                    event.stopPropagation();
                    requestClose(tab.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    requestClose(tab.id);
                  }}
                  data-testid={`button-close-tab-${tab.id}`}
                >
                  <X className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <UnsavedChangesDialog
        open={closing !== null}
        testId="dialog-confirm-close-tab"
        title={t('shell:tabs.unsaved')}
        description={t('shell:tabs.close_description')}
        discardLabel={t('shell:tabs.close_without_saving')}
        saveLabel={t('shell:tabs.save_and_close')}
        snapshot={snapshot}
        showGroupNames={false}
        saveRegion={saveRegion}
        discardRegion={discardRegion}
        onCancel={() => {
          setClosing(null);
        }}
        onProceed={() => {
          if (closing) {
            leave(closing);
            closeTab(closing);
          }
          setClosing(null);
        }}
      />
    </>
  );
}
